import { LegalPageLayout, LegalSection } from "@/components/legal/LegalPageLayout";

export default function PrivacyPolicy() {
  return (
    <LegalPageLayout eyebrow="Legal" title="Privacy Policy">
      <LegalSection heading="1. Overview" testid="privacy-section-overview">
        <p>
          Off-Grid Hydrogen Ltd. ("Off-Grid Hydrogen," "we," "us," or "our") designs and integrates
          hybrid solar, wind, battery, and hydrogen energy systems for agricultural operations in
          British Columbia. This Privacy Policy explains how we collect, use, disclose, and
          safeguard information when you visit our website or submit a site assessment request.
        </p>
        <p>
          We are committed to handling personal information in accordance with the Personal
          Information Protection and Electronic Documents Act (PIPEDA) and applicable British
          Columbia privacy legislation.
        </p>
      </LegalSection>

      <LegalSection heading="2. Information We Collect" testid="privacy-section-collect">
        <p>We collect information in the following ways:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <span className="text-ink font-medium">Site assessment requests.</span> When you submit
            our contact form, we collect your name, company or farm name, email address, phone
            number, BC region, operation type, current power supply, estimated peak demand, and any
            details you choose to share in the message field.
          </li>
          <li>
            <span className="text-ink font-medium">Automatically collected data.</span> Like most
            websites, our server and analytics tools may log standard technical information such as
            browser type, device type, general location (city/region level), and pages visited, to
            help us understand site usage.
          </li>
        </ul>
        <p>We do not knowingly collect information from children, and this site is not directed at children.</p>
      </LegalSection>

      <LegalSection heading="3. How We Use Your Information" testid="privacy-section-use">
        <p>We use the information collected to:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Respond to your site assessment request and evaluate the feasibility of a hybrid energy system for your operation;</li>
          <li>Communicate with you about your inquiry, including by phone or email;</li>
          <li>Improve our website content and understand how visitors use our site;</li>
          <li>Meet legal, regulatory, and contractual obligations.</li>
        </ul>
        <p>We do not use your information for automated decision-making that produces legal or similarly significant effects.</p>
      </LegalSection>

      <LegalSection heading="4. How We Share Information" testid="privacy-section-share">
        <p>
          We do not sell or rent your personal information. We may share information with:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Engineering, technology, or supplier partners directly involved in evaluating or delivering a system you have inquired about, on a need-to-know basis;</li>
          <li>Service providers who support our website, hosting, or communications infrastructure, under confidentiality obligations;</li>
          <li>Regulatory or government bodies where required by law.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="5. Data Retention" testid="privacy-section-retention">
        <p>
          We retain site assessment inquiries for as long as reasonably necessary to respond to your
          request, maintain business records, and comply with legal obligations. You may request
          deletion of your information at any time as described in Section 6.
        </p>
      </LegalSection>

      <LegalSection heading="6. Your Rights" testid="privacy-section-rights">
        <p>Subject to applicable law, you have the right to:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Request access to the personal information we hold about you;</li>
          <li>Request correction of inaccurate or incomplete information;</li>
          <li>Withdraw consent or request deletion of your information, subject to legal or contractual retention requirements;</li>
          <li>Ask questions about how your information is handled.</li>
        </ul>
        <p>To exercise any of these rights, contact us using the details in Section 9.</p>
      </LegalSection>

      <LegalSection heading="7. Security" testid="privacy-section-security">
        <p>
          We use reasonable administrative and technical safeguards to protect the information you
          provide. However, no method of transmission or storage is completely secure, and we
          cannot guarantee absolute security.
        </p>
      </LegalSection>

      <LegalSection heading="8. Cookies" testid="privacy-section-cookies">
        <p>
          Our website uses a limited number of cookies to support core functionality and basic
          analytics. See our{" "}
          <a href="/cookies" className="text-teal underline">Cookie Policy</a> for details.
        </p>
      </LegalSection>

      <LegalSection heading="9. Contact Us" testid="privacy-section-contact">
        <p>
          If you have questions about this Privacy Policy or wish to exercise your privacy rights,
          contact us at:
        </p>
        <p className="text-ink">
          Off-Grid Hydrogen Ltd.<br />
          British Columbia, Canada<br />
          Email: <a href="mailto:info@offgridhydrogen.ca" className="text-teal underline">info@offgridhydrogen.ca</a>
        </p>
      </LegalSection>

      <LegalSection heading="10. Changes to This Policy" testid="privacy-section-changes">
        <p>
          We may update this Privacy Policy from time to time to reflect changes in our practices
          or legal requirements. The "Last updated" date at the top of this page indicates when
          this policy was last revised.
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
}
